/** The landing page: what the app does in one sentence, a way straight to a
 *  model's prices, four live numbers, the four tools with the question each
 *  answers, and this week from Trending. Every number and line is read from
 *  the same endpoints the pages use; nothing here is written by hand. */
import { BLURB } from './blurbs'
import { track } from './track'
import { useEffect, useRef, useState, type JSX } from 'react'
import { api } from '../api'
import { useSettings, useSite, VENDOR_LABEL } from '../settings'
import type { CatalogModel, NewsFeed } from '../types'
import { AskIcon, AssessmentIcon, CalculatorIcon, CompareIcon } from './icons'
import { rememberAssessmentContext } from './AssessmentPage'
import { useAsync } from './useAsync'

type Go = (tab: 'calculator' | 'compare' | 'fit' | 'chat' | 'news' | 'assessment') => void

const still = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** A number that counts to its value: from 0 when it first arrives, from the
 *  old value when a refresh changes it. */
function Count({ to }: { to: number | null }) {
  const [shown, setShown] = useState<number | null>(null)
  const from = useRef(0)
  useEffect(() => {
    if (to == null) return
    if (still()) { from.current = to; setShown(to); return }
    const start = from.current, t0 = performance.now(), ms = 900
    let raf = 0
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / ms)
      const v = Math.round(start + (to - start) * (1 - Math.pow(1 - k, 3)))
      setShown(v)
      if (k < 1) raf = requestAnimationFrame(step); else from.current = to
    }
    raf = requestAnimationFrame(step)
    return () => { cancelAnimationFrame(raf); from.current = to }
  }, [to])
  return <>{shown == null ? '—' : shown.toLocaleString()}</>
}

/** The time left until the next scheduled refresh, in hours and minutes. */
function Countdown({ until }: { until: number }) {
  const [now, setNow] = useState(() => Date.now() / 1000)
  useEffect(() => { const t = setInterval(() => setNow(Date.now() / 1000), 10_000); return () => clearInterval(t) }, [])
  const left = Math.max(0, until - now)
  if (left < 60) return <>any moment</>
  const h = Math.floor(left / 3600), m = Math.floor((left % 3600) / 60)
  return <>{h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m} min`}</>
}

/** One name at a time from a list, changing every couple of seconds. */
function Rotating({ words }: { words: string[] }) {
  const [i, setI] = useState(0)
  useEffect(() => {
    if (words.length < 2) return
    const t = setInterval(() => setI((x) => (x + 1) % words.length), 2200)
    return () => clearInterval(t)
  }, [words.length])
  // the name has a line of its own, so a long one never wraps the label and nothing beside it moves
  return <span className="rotslot"><span className="rot" key={i}>{words[i % words.length]}</span></span>
}

/** Find a model and go to its comparison. */
function ModelSearch({ onPick, onBrowse, onAsk }: { onPick: (m: CatalogModel) => void; onBrowse: () => void; onAsk?: () => void }) {
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [at, setAt] = useState(0)
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 200); return () => clearTimeout(t) }, [q])
  const found = useAsync<CatalogModel[]>(
    () => debounced ? api.searchModels({ q: debounced, limit: 8 }) : Promise.resolve([]), [debounced])
  const list = found.data ?? []
  useEffect(() => { setAt(0) }, [debounced])
  const box = useRef<HTMLFormElement>(null)
  useEffect(() => {
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [])
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (list.length) onPick(list[Math.min(at, list.length - 1)])
    else if (!q.trim()) onBrowse()
  }
  const key = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setAt((x) => Math.min(list.length - 1, x + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAt((x) => Math.max(0, x - 1)) }
    else if (e.key === 'Escape') setOpen(false)
  }
  const show = open && debounced !== ''
  return (
    <form className="finder" ref={box} onSubmit={submit} role="search" aria-labelledby="home-model-label">
      <label id="home-model-label" htmlFor="home-model" className="flabel">Start with a model</label>
      <div className="field">
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true) }} onFocus={() => setOpen(true)} onKeyDown={key}
          id="home-model" placeholder="Llama 4 Maverick, gpt-oss-120b, Qwen3…"
          role="combobox" aria-expanded={show} aria-controls="home-models" aria-autocomplete="list" autoComplete="off" />
        {show ? (
          <ul id="home-models" role="listbox">
            {list.map((m, i) => (
              <li key={m.canonical} role="option" aria-selected={i === at}
                onMouseEnter={() => setAt(i)} onMouseDown={(e) => { e.preventDefault(); onPick(m) }}>
                <span className="nm">{m.canonical}</span>
                <span className="muted small">
                  {m.params_b != null ? `${m.params_b}B` : ''}{m.sold_by_cloud ? ' · sold per token' : ' · self-host'}
                </span>
              </li>
            ))}
            {!list.length ? <li className="none muted small">{found.loading ? 'searching…' : `No model matches “${debounced}”.`}</li> : null}
          </ul>
        ) : null}
      </div>
      <button className="primary" type="submit">Compare prices</button>
      {onAsk ? <button className="second" type="button" onClick={onAsk}>Ask a question</button> : null}
    </form>
  )
}

/** Three lines from Trending: what prices did, the change that matters most, what is retiring. */
function ThisWeek({ feed, go }: { feed: NewsFeed; go: Go }) {
  const lead = [...feed.results]
    .filter((i) => (i.score ?? 0) > 0 && !i.kind.startsWith('price_') && i.kind !== 'retirement')
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0) || b.happened_at - a.happened_at)[0]
  const retiring = feed.retiring ?? []
  const priced = retiring.filter((r) => r.priced).length
  const lines: { text: string }[] = []
  const first = feed.lede?.headline ?? feed.summary[0]
  if (first) lines.push({ text: first.replace(/\.$/, '') })
  if (lead) lines.push({ text: lead.headline.split('\n')[0] })
  if (retiring.length) {
    lines.push({
      text: `${retiring.length} model${retiring.length === 1 ? '' : 's'} retire${retiring.length === 1 ? 's' : ''} in the next 90 days`
        + (priced ? `, ${priced} of them priced here` : '') })
  }
  if (!lines.length) return null
  return (
    <section className="week">
      <div className="head">
        <h3>This week</h3>
        <button type="button" className="linklike" onClick={() => go('news')}>See everything on Trending →</button>
      </div>
      <ul>
        {lines.map((l) => (
          <li key={l.text}><button type="button" className="line" onClick={() => go('news')}><span className="ico" aria-hidden="true" /><span>{l.text}</span><span className="arrow" aria-hidden="true">→</span></button></li>
        ))}
      </ul>
    </section>
  )
}

export function HomePage({ go, compare, chat }: { go: Go; compare: (canonical: string) => void; chat: boolean }) {
  const { vendors, assessment } = useSettings()
  const site = useSite()
  // the fourth square: the assessment, while the administrator offers it (free, or at a price)
  const assessed = Boolean(assessment?.enabled && (assessment.free || assessment.price_usd))
  // re-read every minute, so a refresh that lands moves the numbers while the page is open
  const [tick, setTick] = useState(0)
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 60_000); return () => clearInterval(t) }, [])
  const fresh = useAsync(() => api.freshness(), [tick])
  const status = useAsync(() => api.adminStatus(), [tick])
  const feed = useAsync<NewsFeed>(() => api.newsFeed({ days: 7, limit: 1000 }), [])
  const providers = fresh.data?.providers ?? []
  const prices = providers.reduce((n, p) => n + p.records, 0)
  const models = status.data ? Object.values(status.data.models).reduce((a, b) => a + b, 0) : null
  const auto = fresh.data?.auto_refresh !== false
  const nextAt = auto ? fresh.data?.next_refresh_at ?? null : null
  const clouds = vendors.map((p) => VENDOR_LABEL[p])
  const cloudList = clouds.length > 1 ? `${clouds.slice(0, -1).join(', ')} and ${clouds[clouds.length - 1]}` : clouds[0] ?? ''
  const cards: { id: 'calculator' | 'compare' | 'chat'; label: string; question: string; Icon: () => JSX.Element; text: string }[] = [
    { id: 'calculator', label: 'Calculator', question: 'What will it cost?', Icon: CalculatorIcon, text: 'One workload priced on every cloud and mode, and the volume where the winner changes.' },
    { id: 'compare', label: 'Compare', question: 'Who sells it, where?', Icon: CompareIcon, text: 'A model on every cloud: per token, self-hosted and dedicated, and the hardware that holds it.' },
    { id: 'chat', label: 'Ask', question: 'Ask in plain words', Icon: AskIcon, text: BLURB.chat },
  ]
  return (
    <div className="home">
      <section className="hero">
        <h1>What does it cost to run an LLM?</h1>
        <p className="lede">
          Per token, on your own GPUs, or on a dedicated cluster. Live list prices across {cloudList}, in the region you choose.
        </p>
        <div data-tour="home-search"><ModelSearch onPick={(m) => { track('model_pick', m.canonical, '', { model: m.canonical }); compare(m.canonical) }} onBrowse={() => go('compare')}
          onAsk={chat ? () => go('chat') : undefined} /></div>
      </section>

      {/* the numbers stand on their own, and move: they count up, the cloud
          names turn over, and the last one counts down to the next refresh */}
      <section className="numbers" aria-label="The data behind the app" data-tour="home-numbers">
        <div>
          <b><Count to={prices || null} /></b>
          <span><i className={fresh.data?.stale ? 'pulse stale' : 'pulse'} aria-hidden="true" />Prices tracked</span>
        </div>
        <div>
          <b><Count to={clouds.length} /></b>
          <span>Vendors priced<Rotating words={clouds} /></span>
        </div>
        <div>
          <b><Count to={models} /></b>
          <span>Models sized</span>
        </div>
        <div>
          <b>{(() => { const h = providers.length ? Math.min(...providers.map((p) => p.age_hours)) : null; return h == null ? '—' : h < 1 ? `${Math.max(1, Math.round(h * 60))} min` : h < 48 ? `${Math.round(h)} h` : `${Math.round(h / 24)} d` })()}</b>
          <span>{fresh.data?.stale ? 'Since the last refresh · stale' : nextAt ? <>Since the last refresh · next in <Countdown until={nextAt} /></> : 'Since the last refresh'}</span>
        </div>
      </section>

      <section className="tools" data-tour="home-tools">
        {cards.filter((c) => c.id !== 'chat' || chat).map((c) => (
          <button key={c.id} type="button" className="tool" onClick={() => go(c.id)}>
            <span className="ico"><c.Icon /></span>
            <span className="name">{c.question}</span>
            <span className="text">{c.text}</span>
            <span className="open">Open {c.label} →</span>
          </button>
        ))}
        {assessed ? (
          // from here no comparison is carried: one left over from a tool page would not be this visitor's
          <button type="button" className="tool" onClick={() => { rememberAssessmentContext('', 'home'); go('assessment') }}>
            <span className="ico"><AssessmentIcon /></span>
            <span className="name">Have it checked for you <span className="tag">{assessment?.free ? 'Free' : `$${assessment?.price_usd}`}</span></span>
            <span className="text">A written recommendation for your real workload from the {site.name} team, after a short call to understand it.</span>
            <span className="open">Request an assessment →</span>
          </button>
        ) : null}
      </section>

      {feed.data ? <div data-tour="home-week"><ThisWeek feed={feed.data} go={go} /></div> : null}

    </div>
  )
}
